// Exercise catalog + favorites hooks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { exercisesApi, qk } from "../api";
import type { CustomExerciseInput, Exercise, ExerciseFilter } from "../api";

export function useExercises(filter: ExerciseFilter = {}) {
  return useQuery({
    queryKey: qk.exercises.list(filter),
    queryFn: ({ signal }) => exercisesApi.list(filter, signal),
  });
}

export function useExercise(id: number | undefined) {
  return useQuery({
    queryKey: qk.exercises.detail(id ?? -1),
    queryFn: ({ signal }) => exercisesApi.get(id as number, signal),
    enabled: id !== undefined && id >= 0,
  });
}

export function useFavorites() {
  return useQuery({
    queryKey: qk.favorites.root,
    queryFn: ({ signal }) => exercisesApi.listFavorites(signal),
  });
}

export function useCreateCustomExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomExerciseInput) => exercisesApi.createCustom(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.exercises.root });
    },
  });
}

/** Add/remove favorite — optimistic against the favorites list. */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? exercisesApi.addFavorite(id) : exercisesApi.removeFavorite(id),
    onMutate: async ({ id, on }) => {
      await qc.cancelQueries({ queryKey: qk.favorites.root });
      const prev = qc.getQueryData<Exercise[]>(qk.favorites.root);
      if (prev && !on) {
        qc.setQueryData<Exercise[]>(
          qk.favorites.root,
          prev.filter((e) => e.id !== id),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.favorites.root, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.favorites.root });
      void qc.invalidateQueries({ queryKey: qk.exercises.root });
    },
  });
}
